/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useReducer, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import Icon from '@material-ui/core/Icon'
import classnames from 'classnames'

import reducer from './reducer'
import CameraSwitch from '../../components/CameraSwitch'
import CameraStream from '../../components/CameraStream'
import CameraSetting from '../../components/CameraSetting'
import CameraTestPanel from '../../components/CameraTestPanel'

import MainHeader from '../../components/MainHeader'

import ErrorList from '../../constants/cameraErrorList.js'
import RemoteWatchArea from '../../components/RemoteWatchArea'
import SurveyDialog from '../../components/SurveyDialog.js'
import CameraTutorials from '../../components/CameraTutorials'
import SignInOtherDialog from '../../components/SignInOtherDialog'

import '../../styles/camera.scss'

import { useState } from 'react'

const throttle = require('lodash.throttle')

let isGrtCameraActiveSend = false

export default function Camera(props) {
    const [state, dispatch] = useReducer(reducer, {
        isGrantedCameraPermission:
            props.cameraContext.isGrantedCameraPermission,
        isGrantedMicrophonePermission:
            props.cameraContext.isGrantedMicrophonePermission,
        cameraList: [],
        microphoneList: [],

        cameraError: null,
        process: '',

        stream: null,
        streamList: [],

        isShowSettingDialog: false,

        isShowTestPanel: false,

        isCameraActive: false,
    })

    const [reloadFeatureTime, setReloadFeatureTime] = useState(null)
    const cameraSwitchRef = useRef()
    const motionDetectionRef = useRef()

    useEffect(() => {
        async function main() {
            if (!props.deviceContext.isLogin) {
                props.routerContext.setPath('')
            }

            let { isPreview } = props.cameraContext
            try {
                if (isPreview) {
                    const {
                        isGrantedCameraPermission,
                        isGrantedMicrophonePermission,
                    } = await initVideoAndAudio()

                    dispatch({
                        type: 'setPermission',
                        payload: {
                            isGrantedCameraPermission,
                            isGrantedMicrophonePermission,
                        },
                    })
                }
                resetSettingsForFreeUser()
                resetSettingsForDetectionSunset()
            } catch (error) {
                handleCameraError(error)
            } finally {
                dispatch({
                    type: 'process',
                    payload: {
                        process: 'done',
                    },
                })

                await props.deviceContext.mutations.initialCamera()
                await props.imConnectionContext.mutations.initial()

                dispatch({
                    type: 'cameraActive',
                })
            }
        }
        main()

        return () => {
            state.streamList.forEach((stream) => {
                const tracks = stream.getTracks()
                tracks.forEach(function (track) {
                    track.stop()
                })
            })
            // If camera unset zoomInLock and reload page
            // after init, camera should zoom out
            if (
                props.deviceContext.zoomInLock === false &&
                props.deviceContext.isZoom === true
            ) {
                props.deviceContext.mutations.forceZoomOut()
            }
            props.imConnectionContext.mutations.disconnect()
        }
    }, [])

    useEffect(() => {
        if (reloadFeatureTime) {
            props.deviceContext.mutations.reloadFeature().then(() => {
                const {
                    sensitivity,
                    isNightVision,
                    enableDetection,
                    detectionMode,
                    isZoom,
                } = props.cameraContext

                resetSettingsForFreeUser()
                resetSettingsForDetectionSunset()

                window.eventManager.sendGrtCameraActive({
                    type: 'reload_feature',
                    sensitivity,
                    xmppOnline: true,
                    lowLight: isNightVision,
                    zoomIn: isZoom,
                    enableDetection,
                    detectionMode,
                })
            })
        }
    }, [reloadFeatureTime])

    useEffect(() => {
        const reloadTime =
            window.env === 'prod' ? 12 * 60 * 60 * 1000 : 60 * 1000
        const reloadInterval = setInterval(() => {
            setReloadFeatureTime(Date.now())
        }, reloadTime)

        return () => {
            clearInterval(reloadInterval)
        }
    }, [])

    useEffect(() => {
        const offlineEvent = props.imConnectionContext.mutations.offline
        const onlineEvent = (e) => {
            setTimeout(async () => {
                props.imConnectionContext.mutations.online()
                await props.deviceContext.mutations.initialCamera()
            }, 1500)
        }

        const checkMediaThrottle = throttle(checkMedia, 2000)

        navigator.mediaDevices.ondevicechange = function () {
            checkMediaThrottle()
        }

        window.addEventListener('offline', offlineEvent)
        window.addEventListener('online', onlineEvent)

        return () => {
            window.removeEventListener('online', onlineEvent)
            window.removeEventListener('offline', offlineEvent)
        }
    }, [])

    useEffect(() => {
        if (
            !isGrtCameraActiveSend &&
            state.stream &&
            props.imConnectionContext.status === 'online'
        ) {
            isGrtCameraActiveSend = true
            const {
                sensitivity,
                isNightVision,
                enableDetection,
                detectionMode,
            } = props.cameraContext
            const { isZoom } = props.deviceContext
            window.eventManager.sendGrtCameraActive({
                sensitivity,
                xmppOnline: true,
                lowLight: isNightVision,
                zoomIn: isZoom,
                enableDetection,
                detectionMode,
            })
        }
    }, [state.stream, props.imConnectionContext.status])

    function resetSettingsForFreeUser() {
        if (props.deviceContext.isFree === true) {
            if (props.cameraContext.detectionMode === 'person') {
                props.cameraContext.mutations.updateStatus({
                    detectionMode: 'motion',
                })
            }
            if (props.deviceContext.isZoom === true) {
                props.deviceContext.mutations.forceZoomOut()
            }
            if (props.deviceContext.zoomInLock === true) {
                props.deviceContext.mutations.unsetZoomInLock()
            }
        }
    }

    function resetSettingsForDetectionSunset() {
        props.cameraContext.mutations.updateStatus({
            enableDetection: false,
        })
    }

    useEffect(() => {
        props.cameraContext.mutations.updateStatus({
            error: state.cameraError,
        })
    }, [state.cameraError])

    useEffect(() => {
        async function main() {
            try {
                if (props.cameraContext.isPreview) {
                    const {
                        isGrantedCameraPermission,
                        isGrantedMicrophonePermission,
                    } = await initVideoAndAudio()

                    dispatch({
                        type: 'setPermission',
                        payload: {
                            isGrantedCameraPermission,
                            isGrantedMicrophonePermission,
                        },
                    })
                }
            } catch (error) {
                handleCameraError(error)
            } finally {
                dispatch({
                    type: 'process',
                    payload: {
                        process: 'done',
                    },
                })
            }
        }
        if (state.process === 'done') {
            main()
        }
    }, [props.cameraContext.isPreview])

    useEffect(() => {
        async function main() {
            let { selectedCamera, selectedMicrophone, videoRatio } =
                props.cameraContext

            await requestVideoAndAudioStream({
                selectedCamera,
                selectedMicrophone,
                videoRatio,
            })

            // free to premium: reload feature
            if (props.deviceContext.isFree === false) {
                if (props.deviceContext.featureSource === 'reload') {
                    return window.location.reload()
                }
            } else {
                if (props.cameraContext.detectionMode === 'person') {
                    props.cameraContext.mutations.updateStatus({
                        detectionMode: 'motion',
                    })
                }
            }
        }
        if (state.process === 'done') {
            try {
                main()
            } catch (error) {
                handleCameraError(error)
            }
        }
    }, [props.deviceContext.isFree, props.cameraContext.isAudio])

    function handleCameraError(error) {
        if (
            error === ErrorList.NotGrandVideoPermission ||
            error === ErrorList.NoCamera
        ) {
            dispatch({
                type: 'cameraError',
                payload: {
                    cameraError: error,
                },
            })
            return true
        }
        return false
    }

    async function checkCameraAvailable() {
        if (navigator.mediaDevices?.enumerateDevices) {
            const devices = await navigator.mediaDevices.enumerateDevices()
            if (devices.filter((device) => device.kind === 'videoinput') <= 0) {
                return false
            }
        }
        return true
    }

    async function initVideoAndAudio() {
        let {
            isAudio,
            selectedCamera: restoreSelectedCamera,
            selectedMicrophone: restoreSelectedMicrophone,
            isGrantedMicrophonePermission: preGrantedMicrophonePermission,
        } = props.cameraContext

        if (!(await checkCameraAvailable())) {
            throw ErrorList.NoCamera
        }
        const { isGrantedCameraPermission, isGrantedMicrophonePermission } =
            await requestVideoAndAudioPermission()
        if (
            preGrantedMicrophonePermission === false &&
            isGrantedMicrophonePermission === true
        ) {
            isAudio = true
        }
        if (isGrantedMicrophonePermission === false && isAudio === true) {
            isAudio = false
        }

        const { cameraList, microphoneList } = await reloadMediaList(
            isGrantedCameraPermission,
            isGrantedMicrophonePermission
        )

        let selectedCamera = cameraList && cameraList[0]
        if (restoreSelectedCamera) {
            const camera =
                cameraList &&
                cameraList.find(
                    ({ deviceId }) =>
                        deviceId === restoreSelectedCamera.deviceId
                )
            if (camera) {
                selectedCamera = camera
            }
        }
        if (!selectedCamera && isGrantedCameraPermission) {
            throw ErrorList.NoCamera
        }

        let selectedMicrophone = null
        if (isGrantedMicrophonePermission) {
            selectedMicrophone = microphoneList && microphoneList[0]
            if (restoreSelectedMicrophone) {
                const mic =
                    microphoneList &&
                    microphoneList.find(
                        ({ deviceId }) =>
                            deviceId === restoreSelectedMicrophone.deviceId
                    )
                if (mic) {
                    selectedMicrophone = mic
                }
            }
        }

        props.cameraContext.mutations.updateStatus({
            selectedCamera,
            selectedMicrophone,
            isGrantedCameraPermission,
            isGrantedMicrophonePermission,
            isAudio,
        })

        await requestVideoAndAudioStream({
            selectedCamera,
            selectedMicrophone,
            isAudio,
        })

        return { isGrantedCameraPermission, isGrantedMicrophonePermission }
    }

    async function checkMedia() {
        const { selectedCamera, selectedMicrophone, isAudio } =
            props.cameraContext

        const { pushErrorMessage } = props.errorContext

        await reloadMediaList()

        const isValidCamera = await checkCameraValid(selectedCamera, {})

        try {
            if (!isValidCamera) {
                throw ErrorList.NoCamera
            }

            if (isAudio) {
                if (selectedMicrophone === null) {
                    return
                }
                const isValidMicrophone = await checkMicrophoneValid(
                    selectedMicrophone
                )
                if (!isValidMicrophone) {
                    props.cameraContext.mutations.updateStatus({
                        isAudio: false,
                    })
                    pushErrorMessage({
                        errorKey: 'CannotAccessMicrophone',
                        replaceArgs: {
                            microphoneName:
                                selectedMicrophone && selectedMicrophone.label,
                        },
                    })
                } else {
                    await requestVideoAndAudioStream({
                        selectedCamera,
                        selectedMicrophone,
                    })
                }
            } else {
                await requestVideoAndAudioStream({
                    selectedCamera,
                })
            }
        } catch (error) {
            handleCameraError(error)
        }
    }

    async function reloadCameraStream({
        selectedCameraId = null,
        selectedMicrophoneId = null,
        videoRatio = null,
    }) {
        const { cameraList, microphoneList } = state

        const {
            selectedCamera: prevSelectedCamera,
            selectedMicrophone: prevSelectedMicrophone,
            videoRatio: prevVideoRatio,
        } = props.cameraContext

        if (videoRatio === null) {
            videoRatio = prevVideoRatio
        }
        if (selectedCameraId === null) {
            selectedCameraId = prevSelectedCamera && prevSelectedCamera.deviceId
        }
        if (selectedMicrophoneId === null) {
            selectedMicrophoneId =
                prevSelectedMicrophone && prevSelectedMicrophone.deviceId
        }

        const selectedCamera = cameraList.find(
            (camera) => camera.deviceId === selectedCameraId
        )
        if (!selectedCamera) {
            return
        }
        const selectedMicrophone = microphoneList.find(
            (mic) => mic.deviceId === selectedMicrophoneId
        )

        props.cameraContext.mutations.updateStatus({
            selectedCamera,
            selectedMicrophone,
            videoRatio,
        })

        await requestVideoAndAudioStream({
            selectedCamera,
            selectedMicrophone,
            videoRatio,
        })

        if (
            prevVideoRatio !== videoRatio ||
            prevSelectedCamera !== selectedCamera
        ) {
            props.deviceContext.mutations.forceZoomOut()
        }

        props.errorContext.pushErrorMessage({
            errorKey: 'switchCamera',
            replaceArgs: {
                deviceLabel: selectedCamera.label,
            },
        })
    }

    async function selectMicrophone(selectedMicrophoneId) {
        const { microphoneList } = state

        const {
            selectedCamera,
            videoRatio,
            mutations: { updateStatus },
        } = props.cameraContext

        const selectedMicrophone = microphoneList.find(
            (mic) => mic.deviceId === selectedMicrophoneId
        )
        if (!selectedMicrophone) {
            return
        }

        await requestVideoAndAudioStream({
            selectedCamera,
            selectedMicrophone,
            videoRatio,
        })

        updateStatus({
            selectedMicrophone,
        })

        props.errorContext.pushErrorMessage({
            errorKey: 'switchMicrophone',
            replaceArgs: {
                deviceLabel: selectedMicrophone.label,
            },
        })
    }

    async function updateState(updateObject) {
        try {
            if (updateObject === undefined) {
                return
            }

            if (
                updateObject['selectedCameraId'] !== undefined ||
                updateObject['videoRatio'] !== undefined
            ) {
                await reloadCameraStream({
                    selectedCameraId: updateObject['selectedCameraId'],
                    selectedMicrophoneId: updateObject['selectedMicrophoneId'],
                    videoRatio: updateObject['videoRatio'],
                })
            } else if (updateObject['selectedMicrophoneId'] !== undefined) {
                await selectMicrophone(updateObject['selectedMicrophoneId'])
            }

            const data = {}
            const fields = [
                'isAudio',
                'detectionMode',
                'enableDetection',
                'sensitivity',
            ]

            for (const key of Object.keys(updateObject)) {
                if (fields.some((field) => field === key)) {
                    Object.assign(data, {
                        [key]: updateObject[key],
                    })
                }
            }

            if (Object.keys(data).length > 0) {
                props.cameraContext.mutations.updateStatus(data)
            }
        } catch (error) {
            handleCameraError(error)
        }
    }

    async function requestVideoAndAudioPermission() {
        let isGrantedCameraPermission = false
        let isGrantedMicrophonePermission = false

        const { isPreview } = props.cameraContext
        if (!isPreview) {
            return {
                isGrantedCameraPermission: true,
                isGrantedMicrophonePermission,
            }
        }

        try {
            if (navigator.permissions) {
                const { state } = await navigator.permissions.query({
                    name: 'camera',
                })
                if (state === 'prompt') {
                    dispatch({
                        type: 'process',
                        payload: {
                            process: 'waiting',
                        },
                    })
                    const stream = await navigator.mediaDevices.getUserMedia({
                        video: true,
                    })
                    const tracks = stream.getTracks()
                    tracks.forEach(function (track) {
                        track.stop()
                    })
                }

                if (state === 'granted') {
                    dispatch({
                        type: 'process',
                        payload: {
                            process: 'done',
                        },
                    })
                }

                if (state === 'denied') {
                    throw ErrorList.NotGrandVideoPermission
                }
            }
            isGrantedCameraPermission = true
        } catch (error) {
            if (!handleCameraError(error)) {
                if (error.message === 'Permission denied by system') {
                    handleCameraError(ErrorList.NoCamera)
                } else if (error.message === 'Permission denied') {
                    handleCameraError(ErrorList.NotGrandVideoPermission)
                }
            }
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            })
            const tracks = stream.getTracks()
            tracks.forEach(function (track) {
                track.stop()
            })
            if (navigator.permissions) {
                const state = await navigator.permissions.query({
                    name: 'microphone',
                })
                if (state === 'denied') {
                    isGrantedMicrophonePermission = false
                } else {
                    isGrantedMicrophonePermission = true
                }
            } else {
                isGrantedMicrophonePermission = true
            }
        } catch (error) {
            // user could using without any problem
            window.logManager.error(error)
            isGrantedMicrophonePermission = false
        }
        return {
            isGrantedMicrophonePermission,
            isGrantedCameraPermission,
        }
    }

    async function reloadMediaList(
        isGrantedCameraPermission,
        isGrantedMicrophonePermission
    ) {
        if (isGrantedCameraPermission === undefined) {
            isGrantedCameraPermission = state.isGrantedCameraPermission
            isGrantedMicrophonePermission = state.isGrantedMicrophonePermission
        }

        let deviceList = await navigator.mediaDevices.enumerateDevices()
        deviceList = deviceList.filter(
            (device) => device.deviceId !== 'default'
        )

        let cameraList = []
        let microphoneList = []

        if (isGrantedCameraPermission) {
            await Promise.all(
                deviceList.map(async (device) => {
                    let isValidCamera =
                        device &&
                        device.deviceId &&
                        device.kind === 'videoinput'
                    if (isValidCamera === false) {
                        return
                    }

                    if (isValidCamera) {
                        cameraList.push(device)
                    }
                })
            )
        }
        if (isGrantedMicrophonePermission) {
            await Promise.all(
                deviceList.map(async (device) => {
                    let isValidMicrophone =
                        device &&
                        device.deviceId &&
                        device.kind === 'audioinput'
                    if (isValidMicrophone === false) {
                        return
                    }

                    isValidMicrophone = await checkMicrophoneValid(device)
                    if (isValidMicrophone) {
                        microphoneList.push(device)
                    }
                })
            )
        }

        if (cameraList.length === 0 && isGrantedCameraPermission) {
            throw ErrorList.NoCamera
        }

        dispatch({
            type: 'deviceList',
            payload: {
                cameraList,
                microphoneList,
            },
        })

        return {
            cameraList,
            microphoneList,
        }
    }

    // only check whether device existed or not
    async function checkCameraValid(device) {
        let result = true
        const constraint = {
            video: {
                width: {
                    max: 640,
                },
                height: {
                    max: 480,
                },
                aspectRatio: 4 / 3,
                deviceId: {
                    exact: device.deviceId,
                },
            },
        }
        try {
            window.logManager.log(
                `[Camera] Check HD:`,
                JSON.stringify(constraint)
            )
            const stream = await navigator.mediaDevices.getUserMedia(constraint)
            const tracks = stream.getTracks()
            tracks.forEach(function (track) {
                track.stop()
            })
        } catch (error) {
            window.logManager.error(error)

            result = false
        }
        return result
    }

    async function checkMicrophoneValid(device) {
        let result = true
        const constraint = {
            audio: {
                deviceId: {
                    exact: device && device.deviceId,
                },
                autoGainControl: false,
                googAutoGainControl: false,
                googAutoGainControl2: false,
                noiseSuppression: false,
                latency: 0,
                volume: 1.0,
            },
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia(constraint)
            const tracks = stream.getTracks()
            tracks.forEach(function (track) {
                track.stop()
            })
        } catch (error) {
            result = false
        }
        return result
    }

    async function requestVideoAndAudioStream({
        selectedCamera,
        selectedMicrophone = null,
        videoRatio = undefined,
        isAudio = undefined,
    }) {
        const { isFree } = props.deviceContext

        const {
            isAudio: prevIsAudio,
            isPreview,
            videoRatio: preVideoRatio,
        } = props.cameraContext
        if (!isPreview) {
            return null
        }

        if (videoRatio === undefined) {
            videoRatio = preVideoRatio
        }
        if (isAudio === undefined) {
            isAudio = prevIsAudio
        }
        let videoWidth = !isFree ? 1280 : 640
        let videoHeight = !isFree ? 960 : 480
        let isSupportHD = false

        if (isFree && videoRatio === true) {
            props.cameraContext.mutations.updateStatus({
                videoRatio: false,
            })
            videoRatio = false
        }
        try {
            if (!selectedCamera || !selectedCamera.deviceId) {
                return
            }

            let constraint = {
                video: {
                    width: {
                        max: videoWidth,
                    },
                    height: {
                        max: videoHeight,
                    },
                    aspectRatio: videoRatio ? 16 / 9 : 4 / 3,
                    // product contraint
                    frameRate: { max: 15 },
                    deviceId: {
                        exact: selectedCamera && selectedCamera.deviceId,
                    },
                },
            }

            const isSameStream =
                state.selectedCamera &&
                state.selectedCamera.deviceId === selectedCamera.deviceId
            if (isSameStream && state.stream) {
                isSupportHD = await checkHDSupport({
                    stream: state.stream,
                })
            } else {
                isSupportHD = await checkHDSupport({
                    device: selectedCamera,
                })
            }

            if (isSupportHD && videoRatio) {
                videoWidth = 1280
                videoHeight = 720
                constraint.video.width = {
                    exact: videoWidth,
                }
                constraint.video.height = {
                    exact: videoHeight,
                }
            } else if (!isFree && !videoRatio) {
                videoWidth = 640
                videoHeight = 480
                constraint.video.width = {
                    exact: videoWidth,
                }
                constraint.video.height = {
                    exact: videoHeight,
                }
            }
            if (isAudio && selectedMicrophone) {
                constraint['audio'] = {
                    deviceId: {
                        exact: selectedMicrophone.deviceId,
                    },
                    autoGainControl: false,
                    googAutoGainControl: false,
                    googAutoGainControl2: false,
                    noiseSuppression: false,
                    latency: 0,
                    volume: 1.0,
                }
            }

            let stream = null
            if (isSameStream && state.stream) {
                await state.stream
                    .getVideoTracks()[0]
                    .applyConstraints(constraint.video)

                if (isAudio) {
                    state.stream.removeTrack(state.stream.getAudioTracks()[0])
                    const audioStream =
                        await navigator.mediaDevices.getUserMedia({
                            audio: constraint.audio,
                        })
                    await state.stream.addTrack(audioStream.getAudioTracks()[0])
                }
                stream = state.stream
            } else {
                if (state.stream) {
                    const tracks = state.stream.getTracks()
                    tracks.forEach(function (track) {
                        track.stop()
                    })
                }
                stream = await navigator.mediaDevices.getUserMedia(constraint)
            }

            const videoStreamSettings = stream.getVideoTracks()[0].getSettings()
            let liveWidth = videoStreamSettings.width
            let liveHeight = videoStreamSettings.height

            if (isFree) {
                liveWidth = liveWidth / 2
                liveHeight = liveHeight / 2
            } else {
                if (videoStreamSettings.width === 1280) {
                    liveWidth = liveWidth / 2
                    liveHeight = liveHeight / 2
                }
            }
            props.cameraContext.mutations.updateStatus({
                previewWidth: videoStreamSettings.width,
                previewHeight: videoStreamSettings.height,
                liveWidth,
                liveHeight,
            })

            dispatch({
                type: 'addStream',
                payload: {
                    stream,
                },
            })
            dispatch({
                type: 'cameraError',
                payload: {
                    cameraError: null,
                },
            })
        } catch (error) {
            window.logManager.error(`[Camera] Stream error: ${error}`)
            throw ErrorList.NoCamera
        }
    }
    async function checkHDSupport({ device, stream = null }) {
        let result = false
        if (stream && stream.getVideoTracks()[0]) {
            try {
                await stream.getVideoTracks()[0].applyConstraints({
                    width: 1280,
                    height: 720,
                })
                result = true
            } catch (error) {
                window.logManager.error(`[Camera] Not support HD`)
            }
        } else {
            try {
                const constraint = {
                    video: {
                        width: {
                            max: 1280,
                        },
                        height: {
                            max: 720,
                        },
                        aspectRatio: 16 / 9,
                        deviceId: {
                            exact: device.deviceId,
                        },
                    },
                }
                const stream = await navigator.mediaDevices.getUserMedia(
                    constraint
                )
                const tracks = stream.getTracks()
                tracks.forEach(function (track) {
                    track.stop()
                })
                result = true
            } catch (error) {}
        }
        return result
    }

    function closeSettingDialog() {
        dispatch({
            type: 'closeSettingDialog',
        })
    }

    async function toggleSettingDialog(eventAction) {
        const toggle = !state.isShowSettingDialog
        if (toggle) {
            try {
                await reloadMediaList()
            } catch (error) {
                window.logManager.error(error)
            }
            dispatch({
                type: 'openSettingDialog',
            })
            window.eventManager.sendEngagementBehavior('settings', eventAction)
        } else {
            dispatch({
                type: 'closeSettingDialog',
            })
        }
    }

    function toggleCameraEnabled(eventAction) {
        const { isPreview } = props.cameraContext
        const { updateStatus } = props.cameraContext.mutations
        const isOn = !isPreview
        if (!isOn) {
            dispatch({
                type: 'cameraError',
                payload: {
                    cameraError: null,
                },
            })
        }
        updateStatus({
            isPreview: isOn,
        })
        window.eventManager.sendFunctionBehavior(
            isOn ? 'camera_on' : 'camera_off',
            eventAction
        )
    }

    function changeMotionDection(isOn, eventAction) {
        const { updateStatus } = props.cameraContext.mutations
        const { cameraPreferences } = props.deviceContext
        updateStatus({
            enableDetection: isOn,
        })
        if (isOn && cameraPreferences?.tutorialsStep === 1) {
            props.errorContext.pushErrorMessage({
                errorKey: 'motionOn',
            })
        }
        window.eventManager.sendFunctionBehavior(
            isOn ? 'motion_on' : 'motion_off',
            eventAction
        )
    }

    function render() {
        const {
            stream,
            cameraList,
            microphoneList,
            isGrantedMicrophonePermission,
            cameraError,
            process,
            isShowSettingDialog,
            isShowTestPanel,
            isCameraActive,
        } = state

        const {
            deviceContext,
            imConnectionContext,
            cameraContext,
            errorContext,
        } = props

        const { currentErrorMessage } = errorContext

        const {
            isNightVision,
            isAutoLowLight,

            enableDetection,
            detectionMode,
            sensitivity,
            recordNativeResolution,
            testVideo,
            debugView,
            dbFile,
            runPipeline,
            testImage,
            globalInfereceInterval,

            isPreview,
            isAudio,
            selectedCamera,
            selectedMicrophone,

            isPersonDetectionBox,
            isRelay,
            isPaintSize,
            videoRatio,
            isRecording,
            recordTime,
        } = cameraContext
        let { clickX, clickY, zoomScale, zoomMode, isZoom, isFree, uniqueId } =
            deviceContext

        if (isFree) {
            clickX = 0
            clickY = 0
            zoomScale = 1
            zoomMode = 1
            isZoom = false
        }

        let isPersonDetection = false,
            // due to detection algorithm, motion should always be true
            isMotionDetection = true
        if (enableDetection) {
            if (detectionMode === 'motion') {
                isPersonDetection = false
            } else {
                isPersonDetection = true
            }
        } else {
            isMotionDetection = false
            isPersonDetection = false
        }

        // always set Detection is false by detection sunset
        isMotionDetection = false
        isPersonDetection = false

        let errorComponent = null
        let isShowSignInOtherDialog = false
        let isNotGrandVideoPermission = false
        if (process === 'waiting') {
            errorComponent = (
                <div className="camera-error-section">
                    <p className="camera-error-content">
                        <FormattedMessage id="allowPermissions" />
                    </p>
                </div>
            )
        } else if (
            currentErrorMessage &&
            currentErrorMessage.errorKey === 'xmppConnectionConflict'
        ) {
            isShowSignInOtherDialog = true
        } else if (cameraError === ErrorList.NotGrandVideoPermission) {
            isNotGrandVideoPermission = true
            errorComponent = (
                <div className="camera-error-section">
                    <p className="camera-error-title">
                        <Icon>error</Icon>
                        <span>
                            <FormattedMessage id="allowPermissionsTitle" />
                        </span>
                    </p>
                    <p className="camera-error-content">
                        <span>
                            <FormattedMessage id="allowPermissions" />
                            <a
                                className="link"
                                href="https://url.alfredcamera.app/page/permission-camera_preview-webcam"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FormattedMessage id="knowMore" />
                            </a>
                        </span>
                    </p>
                </div>
            )
        } else if (cameraError === ErrorList.NoCamera) {
            errorComponent = (
                <div className="camera-error-section">
                    <p className="camera-error-title">
                        <Icon>error</Icon>
                        <span>
                            <FormattedMessage id="CameraInaccessible" />
                        </span>
                    </p>
                    <p className="camera-error-content">
                        <FormattedMessage id="CameraInaccessibleDetail" />
                    </p>
                </div>
            )
        } else if (!isPreview) {
            errorComponent = (
                <div className="camera-error-section">
                    <p className="camera-error-content">
                        <FormattedMessage id="camera_please_turn_on" />
                    </p>
                </div>
            )
        }

        const isCameraOpen = isPreview && !errorComponent
        const currentStep = props.deviceContext.cameraPreferences
            ? props.deviceContext.cameraPreferences.tutorialsStep || 0
            : -1

        const renderStream = (
            eventAction,
            cameraSwitchRef,
            motionDetectionRef,
            pureStream = false,
            hideSettings = false,
            hideStats = false
        ) => {
            return (
                <>
                    <CameraSwitch
                        className={classnames('camera-switch', {
                            small: !props.cameraContext.isLived,
                        })}
                        onToggle={() => toggleCameraEnabled(eventAction)}
                        checked={isPreview}
                        cameraSwitchRef={cameraSwitchRef}
                    />
                    <CameraStream
                        key="CameraStream"
                        cameraContext={cameraContext}
                        deviceContext={deviceContext}
                        imConnectionContext={imConnectionContext}
                        sourceStream={stream}
                        videoRatio={videoRatio}
                        isNightVision={isNightVision}
                        isAutoLowLight={isAutoLowLight}
                        isPersonDetection={isPersonDetection}
                        isMotionDetection={isMotionDetection}
                        isPreview={isPreview}
                        sensitivity={sensitivity}
                        isAudio={isAudio}
                        isPersonDetectionBox={isPersonDetectionBox}
                        isRelay={isRelay}
                        isPaintSize={isPaintSize}
                        isFree={isFree}
                        clickX={clickX}
                        clickY={clickY}
                        isZoom={isZoom}
                        zoomMode={zoomMode}
                        zoomScale={zoomScale}
                        isRecording={isRecording}
                        recordTime={recordTime}
                        recordNativeResolution={recordNativeResolution}
                        testVideo={testVideo}
                        debugView={debugView}
                        dbFile={dbFile}
                        runPipeline={runPipeline}
                        testImage={testImage}
                        globalInfereceInterval={globalInfereceInterval}
                        toggleSettingDialog={toggleSettingDialog}
                        changeMotionDection={changeMotionDection}
                        isMotionDetectionOn={enableDetection}
                        errorComponent={errorComponent}
                        motionDetectionRef={motionDetectionRef}
                        pureStream={pureStream}
                        controlsDisabled={
                            props.deviceContext.cameraPreferences
                                ?.tutorialsStep === 0
                        }
                        hideStats={hideStats}
                        hideSettings={hideSettings || isNotGrandVideoPermission}
                        eventAction={eventAction}
                    />
                </>
            )
        }

        return (
            <div
                className={classnames({
                    blur: currentStep >= 0 && currentStep < 3,
                })}
            >
                <MainHeader
                    openTestPanel={() =>
                        dispatch({
                            type: 'openTestPanel',
                        })
                    }
                />
                <SignInOtherDialog open={isShowSignInOtherDialog} />
                <div style={{ display: 'flex' }}>
                    <div
                        className={`camera-container-list camera-container-list_1`}
                        style={{ flex: 1 }}
                    >
                        {renderStream('preview')}
                        <RemoteWatchArea
                            key="RemoteWatchArea"
                            show={!props.cameraContext.isLived}
                        />
                        {props.deviceContext.cameraPreferences && (
                            <CameraTutorials
                                currentStep={currentStep}
                                onUpdated={
                                    props.deviceContext.mutations
                                        .onTutorialsUpdated
                                }
                                disabled={
                                    !isCameraOpen ||
                                    props.imConnectionContext.status !==
                                        'online'
                                }
                                cameraSwitchRef={cameraSwitchRef}
                                motionDetectionRef={motionDetectionRef}
                                setDisabled={(controlsDisabled) =>
                                    dispatch({
                                        type: 'setControlsDisabled',
                                        payload: {
                                            controlsDisabled,
                                        },
                                    })
                                }
                                cameraStreamComponent={renderStream(
                                    `onboard_tutorial_step_${currentStep + 1}`,
                                    cameraSwitchRef,
                                    motionDetectionRef,
                                    true,
                                    true,
                                    true
                                )}
                            />
                        )}

                        <CameraSetting
                            isShowSettingDialog={isShowSettingDialog}
                            closeSettingDialog={closeSettingDialog}
                            isAudio={isAudio}
                            cameraList={cameraList}
                            microphoneList={microphoneList}
                            selectedCamera={selectedCamera}
                            selectedMicrophone={selectedMicrophone}
                            isGrantedMicrophonePermission={
                                isGrantedMicrophonePermission
                            }
                            videoRatio={videoRatio}
                            updateState={(obj) => updateState(obj)}
                            reloadMediaList={reloadMediaList}
                            isFree={isFree}
                        ></CameraSetting>
                        <CameraTestPanel
                            isPaintSize={isPaintSize}
                            isNightVision={isNightVision}
                            isAutoLowLight={isAutoLowLight}
                            isPreview={isPreview}
                            sensitivity={sensitivity}
                            enableDetection={enableDetection}
                            detectionMode={detectionMode}
                            recordNativeResolution={recordNativeResolution}
                            testVideo={testVideo}
                            dbFile={dbFile}
                            debugView={debugView}
                            runPipeline={runPipeline}
                            testImage={testImage}
                            globalInfereceInterval={globalInfereceInterval}
                            isFree={isFree}
                            isOpen={isShowTestPanel}
                            closeDialog={() =>
                                dispatch({
                                    type: 'closeTestPanel',
                                })
                            }
                            updateCameraStatus={
                                cameraContext.mutations.updateStatus
                            }
                        />
                    </div>
                </div>
                {!(
                    currentErrorMessage &&
                    currentErrorMessage.errorKey === 'xmppConnectionConflict'
                ) &&
                    isCameraActive && <SurveyDialog uniqueId={uniqueId} />}
            </div>
        )
    }

    return render()
}
